import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LoadingRegion } from "@/components/ui/loading";
import { useSession } from "./useSession";

/**
 * Gate on a session, with THREE states rather than two.
 *
 * The two-state version — "have a user or redirect to login" — is wrong twice.
 * It flashes the login screen on every reload while the probe is in flight,
 * and it turns a store outage into a sign-out, because an unresolved query
 * looks exactly like an unauthenticated one.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { data, isPending, isError, refetch } = useSession();
  const { pathname, search } = useLocation();

  if (isPending) {
    // Never logged-out content here: this renders only when the first session
    // result is unresolved. A revalidation keeps an authenticated tree mounted,
    // including all local component state, until the server answers.
    return <LoadingRegion label="Loading…" />;
  }

  if (isError && !data) {
    // An initial 503 has no authenticated tree to preserve, but it must remain
    // visibly distinct from the 401 redirect below.
    return <SessionUnavailable retry={() => void refetch()} />;
  }

  if (data === null) {
    // `replace`, or the guarded URL stays in history and the back button
    // bounces between it and /login forever.
    //
    // One encode, applied to the decoded path this router gave us. Encoding a
    // raw `location.search` slice again would double-escape it and the server
    // would reject the result as a malformed return_to.
    return (
      <Navigate
        to={`/login?return_to=${encodeURIComponent(pathname + search)}`}
        replace
      />
    );
  }

  return (
    <>
      <SessionUnavailable visible={isError} retry={() => void refetch()} />
      {children}
    </>
  );
}

function SessionUnavailable({
  visible = true,
  retry,
}: {
  visible?: boolean;
  retry: () => void;
}) {
  if (!visible) return null;
  return (
    <section className="space-y-4 px-4 py-16 sm:px-6">
      <h1 className="text-2xl font-bold tracking-tight">
        Can&apos;t reach Character Studio.
      </h1>
      <p className="max-w-prose text-sm leading-relaxed text-fg-muted">
        You are still signed in — the service just isn&apos;t answering right
        now. This usually clears on its own within a minute.
      </p>
      <Button variant="outline" onClick={retry}>
        Try again
      </Button>
    </section>
  );
}
